/**
 * Clips: property animation, the role Godot's AnimationPlayer plays. A clips
 * file (`*.clips.json`) holds named clips; each clip keyframes fields of the
 * object that plays it, or of objects under it, over time. Times are in
 * milliseconds and eases are Phaser's ease names, as in a tween.
 *
 * An object plays clips from one file through its `clips` field:
 *
 *   { "id": "o4", "type": "container", "name": "Chest", "clips": { "file": "animations/chest.clips.json", "autoplay": "idle" } }
 *
 * This module has the file's types and the parts of playback that need no
 * Phaser: where a clip is at a given moment, a track's value at a time, and
 * which events a stretch of playback passes. The runtime's ClipPlayer uses them.
 */

/** The `clips` field on an object. */
export interface ClipsRef {
  /** Path of the clips file, relative to the project root. */
  file: string;
  /** Clip that starts when the object is built in a running game. */
  autoplay?: string;
}

export interface ClipsFile {
  formatVersion: number;
  clips: Record<string, Clip>;
}

export interface Clip {
  /** Length in ms. */
  duration: number;
  /** Times to play again after the first, as in a tween; -1 repeats forever. Default 0. */
  repeat?: number;
  /** Play back to the start after each forward pass. Default false. */
  yoyo?: boolean;
  /** Pause in ms before each repeat. Default 0. */
  repeatDelay?: number;
  tracks?: ClipTrack[];
}

export type ClipTrack = FieldTrack | PlayTrack | EventTrack | SoundTrack;

/** Keys for one field of one object. */
export interface FieldTrack {
  /** Object names under the clip's owner, joined with `/`. Omitted or empty: the owner itself. */
  target?: string;
  /** A field name as the object's JSON writes it, such as `angle` or `tint`; or a script field's name. */
  field: string;
  /** With `field`, the script (by path) whose field this is. */
  script?: string;
  /** Add the values to the field's value when the clip starts, instead of setting them. */
  relative?: boolean;
  keys: ClipKey[];
}

export interface ClipKey {
  /** Time in ms. */
  t: number;
  v: unknown;
  /** How the value arrives at this key from the one before: a Phaser ease name, or `Step` to hold and jump. Default `Linear`. */
  ease?: string;
}

/** Starts frame animations on a sprite. */
export interface PlayTrack {
  target?: string;
  /** An empty `anim` stops the sprite's animation. */
  play: { t: number; anim: string }[];
}

/** Fires events at times. */
export interface EventTrack {
  target?: string;
  events: ClipEvent[];
}

export interface ClipEvent {
  t: number;
  /** Calls this method on the target's scripts that have it. */
  call?: string;
  /** An event name for listeners, when the event calls nothing. */
  event?: string;
  args?: unknown[];
}

/** Plays sounds from the project's sounds, by name. */
export interface SoundTrack {
  /** Sounds play from the game, not from an object. */
  target?: undefined;
  sounds: { t: number; sound: string }[];
}

export const isFieldTrack = (t: ClipTrack): t is FieldTrack => 'field' in t && typeof t.field === 'string';
export const isPlayTrack = (t: ClipTrack): t is PlayTrack => Array.isArray((t as PlayTrack).play);
export const isEventTrack = (t: ClipTrack): t is EventTrack => Array.isArray((t as EventTrack).events);
export const isSoundTrack = (t: ClipTrack): t is SoundTrack => Array.isArray((t as SoundTrack).sounds);

/** The name an event goes by: its method, or its event name. */
export const clipEventName = (e: ClipEvent): string => e.call ?? e.event ?? '';

/** The ease that holds the previous value until the key's time. */
export const STEP_EASE = 'Step';

/** Ease names the editor offers. Any name Phaser's GetEaseFunction knows also works. */
export const CLIP_EASES: readonly string[] = [
  'Linear',
  STEP_EASE,
  ...['Sine', 'Quad', 'Cubic', 'Quart', 'Quint', 'Expo', 'Circ', 'Back', 'Bounce', 'Elastic'].flatMap((f) => [
    `${f}.easeIn`,
    `${f}.easeOut`,
    `${f}.easeInOut`,
  ]),
];

/** How a field's values blend between keys. */
export type FieldBlend = 'number' | 'color' | 'step';

/** Fields of core objects that clips can animate, and how each blends. */
export const CLIP_FIELDS: Readonly<Record<string, FieldBlend>> = {
  x: 'number',
  y: 'number',
  scaleX: 'number',
  scaleY: 'number',
  angle: 'number',
  alpha: 'number',
  depth: 'number',
  originX: 'number',
  originY: 'number',
  scrollFactorX: 'number',
  scrollFactorY: 'number',
  tint: 'color',
  visible: 'step',
  flipX: 'step',
  flipY: 'step',
  texture: 'step',
  text: 'step',
  // Lights.
  intensity: 'number',
  radius: 'number',
};

/** How a track's values blend: by field for core fields, by value for script fields. */
export function blendOf(track: FieldTrack): FieldBlend {
  if (!track.script && track.field in CLIP_FIELDS) return CLIP_FIELDS[track.field]!;
  return track.keys.every((k) => typeof k.v === 'number') ? 'number' : 'step';
}

/** A clip's timing with defaults filled in. */
export function clipTiming(clip: Clip): { duration: number; repeat: number; yoyo: boolean; repeatDelay: number } {
  return {
    duration: Math.max(0, clip.duration || 0),
    repeat: clip.repeat === -1 ? -1 : Math.max(0, Math.floor(clip.repeat ?? 0)),
    yoyo: !!clip.yoyo,
    repeatDelay: Math.max(0, clip.repeatDelay ?? 0),
  };
}

/** How long a clip plays in total, in ms: Infinity when it repeats forever. */
export function clipLength(clip: Clip): number {
  const { duration, repeat, yoyo, repeatDelay } = clipTiming(clip);
  if (duration === 0) return 0;
  if (repeat === -1) return Infinity;
  return (repeat + 1) * duration * (yoyo ? 2 : 1) + repeat * repeatDelay;
}

/** One stretch of a clip's playback: a forward pass, a yoyo pass back, or the pause before a repeat. */
interface Pass {
  kind: 'forward' | 'back' | 'delay';
  /** Elapsed time the pass starts and ends at. */
  start: number;
  end: number;
  /** Whether a later iteration follows this one. */
  more: boolean;
}

function passAt(clip: Clip, elapsed: number): Pass {
  const { duration: d, repeat, yoyo, repeatDelay } = clipTiming(clip);
  const span = d * (yoyo ? 2 : 1);
  const period = repeatDelay + span;
  // The first iteration has no delay in front of it.
  let k = 0;
  let start = 0;
  if (elapsed >= span) {
    k = 1 + Math.floor((elapsed - span) / period);
    if (repeat !== -1) k = Math.min(k, repeat);
    start = k === 0 ? 0 : span + (k - 1) * period;
  }
  const more = repeat === -1 || k < repeat;
  let at = start;
  if (k > 0 && repeatDelay > 0) {
    if (elapsed < at + repeatDelay) return { kind: 'delay', start: at, end: at + repeatDelay, more };
    at += repeatDelay;
  }
  if (!yoyo || elapsed < at + d) return { kind: 'forward', start: at, end: at + d, more };
  return { kind: 'back', start: at + d, end: at + 2 * d, more };
}

/** Where in the clip (0 to duration) playback is after `elapsed` ms. */
export function clipTimeAt(clip: Clip, elapsed: number): number {
  const { duration: d, yoyo } = clipTiming(clip);
  if (d === 0) return 0;
  if (elapsed >= clipLength(clip)) return yoyo ? 0 : d;
  const pass = passAt(clip, Math.max(0, elapsed));
  if (pass.kind === 'delay') return yoyo ? 0 : d;
  const into = Math.max(0, elapsed) - pass.start;
  return pass.kind === 'forward' ? into : d - into;
}

/**
 * Calls `visit` with the clip time of every event that playback from
 * `from` to `to` ms (elapsed) passes, in order. An event at a pass's start
 * fires at the start, and one at its end fires once, even when a yoyo turns
 * there. `from` itself is included, so playing from 0 fires events at 0;
 * a clip of zero length fires its events when `from` is 0.
 */
export function eventTimesBetween(clip: Clip, times: number[], from: number, to: number, visit: (t: number) => void): void {
  const { duration: d } = clipTiming(clip);
  const length = clipLength(clip);
  if (d === 0 || !times.length) {
    if (d === 0 && from === 0 && to >= 0) for (const t of times) if (t <= 0) visit(t);
    return;
  }
  const sorted = [...times].sort((a, b) => a - b);
  let cursor = Math.max(0, from);
  const stop = Math.min(to, length);
  // Every pass is longer than zero (d > 0), so the cursor always moves.
  while (cursor < stop) {
    const pass = passAt(clip, cursor);
    const end = Math.min(stop, pass.end);
    const complete = end >= pass.end;
    if (pass.kind === 'forward') {
      const a = cursor - pass.start;
      const b = end - pass.start;
      for (const t of sorted) if ((t >= a && t < b) || (complete && t === d && t >= a)) visit(t);
    } else if (pass.kind === 'back') {
      const a = d - (cursor - pass.start);
      const b = d - (end - pass.start);
      const last = complete && !pass.more;
      for (const t of [...sorted].reverse()) {
        if (t > b && t <= a && !(t === d && cursor === pass.start)) visit(t);
        else if (last && t === 0 && b === 0) visit(t);
      }
    }
    cursor = end;
  }
}

/**
 * A field track's value at clip time `time`. `ease` turns an ease name into
 * Phaser's ease function (0 to 1 in, eased 0 to 1 out). Before the first key
 * the track holds the first value, and after the last, the last.
 */
export function sampleTrack(track: FieldTrack, time: number, ease: (name: string) => (p: number) => number): unknown {
  const keys = track.keys;
  if (!keys.length) return undefined;
  if (time <= keys[0]!.t) return keys[0]!.v;
  const last = keys[keys.length - 1]!;
  if (time >= last.t) return last.v;
  let i = 1;
  while (keys[i]!.t < time) i++;
  const a = keys[i - 1]!;
  const b = keys[i]!;
  if (time === b.t) return b.v;
  const blend = blendOf(track);
  const name = b.ease ?? 'Linear';
  if (blend === 'step' || name === STEP_EASE || b.t <= a.t) return a.v;
  const p = ease(name)((time - a.t) / (b.t - a.t));
  if (blend === 'color' && typeof a.v === 'number' && typeof b.v === 'number') return lerpColor(a.v, b.v, p);
  if (typeof a.v === 'number' && typeof b.v === 'number') return a.v + (b.v - a.v) * p;
  return a.v;
}

/** Blends two 0xRRGGBB colours channel by channel. */
export function lerpColor(a: number, b: number, p: number): number {
  const ch = (shift: number) => {
    const x = (a >> shift) & 0xff;
    const y = (b >> shift) & 0xff;
    return Math.max(0, Math.min(255, Math.round(x + (y - x) * p)));
  };
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

/** The value a play track wants at `time`: the last key at or before it, or undefined before the first. */
export function playAt(track: PlayTrack, time: number): string | undefined {
  let anim: string | undefined;
  for (const k of track.play) if (k.t <= time) anim = k.anim;
  return anim;
}

/** Splits a track target into object names. */
export function targetPath(target: string | undefined): string[] {
  return (target ?? '').split('/').filter((s) => s !== '' && s !== '.');
}

/** Sorts each track's keys by time, so files edited by hand play in order. */
export function normalizeClip(clip: Clip): Clip {
  return {
    ...clip,
    tracks: (clip.tracks ?? []).map((t) => {
      if (isFieldTrack(t)) return { ...t, keys: [...(t.keys ?? [])].sort((a, b) => a.t - b.t) };
      if (isPlayTrack(t)) return { ...t, play: [...t.play].sort((a, b) => a.t - b.t) };
      if (isEventTrack(t)) return { ...t, events: [...t.events].sort((a, b) => a.t - b.t) };
      if (isSoundTrack(t)) return { ...t, sounds: [...t.sounds].sort((a, b) => a.t - b.t) };
      return t;
    }),
  };
}
